import image0 from "@/assets/ingredient-scroll/v10-liver-alpha-lipoic-acid.webp.asset.json";
import image1 from "@/assets/ingredient-scroll/v10-liver-curcumin.webp.asset.json";
import image2 from "@/assets/ingredient-scroll/v10-liver-nac.webp.asset.json";
import image3 from "@/assets/ingredient-scroll/v10-liver-probiotics.webp.asset.json";
import image4 from "@/assets/ingredient-scroll/v10-liver-silymarin.webp.asset.json";
import image5 from "@/assets/ingredient-scroll/v10-ova-berberine.webp.asset.json";
import image6 from "@/assets/ingredient-scroll/v10-ova-d3.webp.asset.json";
import image7 from "@/assets/ingredient-scroll/v10-ova-folate.webp.asset.json";
import image8 from "@/assets/ingredient-scroll/v10-ova-inositol.webp.asset.json";
import image9 from "@/assets/ingredient-scroll/v10-ova-magnesium.webp.asset.json";
/**
 * Composition transcribed from the live storefront source at
 * ac6fe7be94d4c1d272ad00bd633a143687840202: productContent.ts quick facts
 * and ClinicalActivesShowcase.tsx. No new efficacy or absorption claims.
 * Revalidate these composition records whenever the approved label changes.
 */
export type IngredientStoryProduct = "liver" | "ova";
export type IngredientStoryItem = {
  shortName: string;
  subtitle: string;
  dose: string;
  image: string;
  alt: string;
  caption: string;
  kicker: string;
  name: string;
  copy: string;
};
export type IngredientStoryContent = {
  name: string;
  eyebrow: string;
  heading: readonly [string, string];
  intro: string;
  selectionHeading: string;
  serving: string;
  ingredients: readonly IngredientStoryItem[];
  otherIngredients: readonly { name: string; dose: string }[];
};
const IMAGES: Record<string, string> = {
  "v10-liver-alpha-lipoic-acid.webp": image0.url,
  "v10-liver-curcumin.webp": image1.url,
  "v10-liver-nac.webp": image2.url,
  "v10-liver-probiotics.webp": image3.url,
  "v10-liver-silymarin.webp": image4.url,
  "v10-ova-berberine.webp": image5.url,
  "v10-ova-d3.webp": image6.url,
  "v10-ova-folate.webp": image7.url,
  "v10-ova-inositol.webp": image8.url,
  "v10-ova-magnesium.webp": image9.url,
};
const asset = (file: string) => IMAGES[file];
export const INGREDIENT_STORIES: Record<IngredientStoryProduct, IngredientStoryContent> = {
  liver: {
    name: "Liver Biome Plus",
    eyebrow: "THE LIVER BIOME PLUS FORMULA",
    heading: ["Five actives.", "One daily formula."],
    intro: "NAC, liposomal silymarin, curcumin and alpha-lipoic acid, with a multi-strain probiotic. Explore each ingredient and its amount per two-tablet serving.",
    selectionHeading: "Five actives. One formula.",
    serving: "Per two tablets",
    ingredients: [
      {
        shortName: "NAC", subtitle: "N-acetyl cysteine", dose: "600 mg",
        image: asset("v10-liver-nac.webp"), alt: "Ingredient-inspired white powder with a clear glass scoop in blue light",
        caption: "N-acetyl cysteine", kicker: "NAC", name: "N-Acetyl Cysteine (NAC)",
        copy: "600 mg of N-acetyl cysteine in each two-tablet serving, alongside the formula's other four actives.",
      },
      {
        shortName: "Silymarin", subtitle: "Liposomal milk thistle", dose: "350 mg eq.",
        image: asset("v10-liver-silymarin.webp"), alt: "Milk thistle flowers, seeds and powder arranged on blue glass",
        caption: "Liposomal silymarin", kicker: "MILK THISTLE", name: "Liposomal Silymarin",
        copy: "Liposomal milk thistle providing 350 mg equivalent silymarin per two-tablet serving. The listed amount is a silymarin equivalent.",
      },
      {
        shortName: "Curcumin", subtitle: "Liposomal form", dose: "100 mg",
        image: asset("v10-liver-curcumin.webp"), alt: "Golden turmeric powder and turmeric pieces on a blue glass surface",
        caption: "Liposomal curcumin", kicker: "LIPOSOMAL FORM", name: "Liposomal Curcumin",
        copy: "100 mg of liposomal curcumin per two-tablet serving. Curcumin is one of the compounds found in turmeric.",
      },
      {
        shortName: "ALA", subtitle: "Liposomal alpha-lipoic acid", dose: "100 mg",
        image: asset("v10-liver-alpha-lipoic-acid.webp"), alt: "Ingredient-inspired pale golden granules on clear glass in blue light",
        caption: "Liposomal alpha-lipoic acid", kicker: "LIPOSOMAL FORM", name: "Liposomal Alpha-Lipoic Acid",
        copy: "100 mg of liposomal alpha-lipoic acid, also called ALA, in each two-tablet serving.",
      },
      {
        shortName: "Probiotics", subtitle: "Multi-strain blend", dose: "10B CFU",
        image: asset("v10-liver-probiotics.webp"), alt: "Ingredient-inspired ivory powder in a clear glass dish on a pale blue surface",
        caption: "Multi-strain probiotic", kicker: "PROBIOTIC BLEND", name: "Multi-Strain Probiotic",
        copy: "10 billion CFU per two-tablet serving at time of manufacture. CFU stands for colony-forming units, the measure used for the probiotic blend.",
      },
    ],
    otherIngredients: [],
  },
  ova: {
    name: "Ova Balance",
    eyebrow: "THE OVA BALANCE FORMULA",
    heading: ["Inside Ova Balance.", "One daily sachet."],
    intro: "A 40:1 myo-inositol and D-chiro-inositol blend, with berberine HCl, active folate, magnesium and vitamin D3. Explore the ingredients in each daily sachet.",
    selectionHeading: "One sachet. A closer look.",
    serving: "Per daily sachet",
    ingredients: [
      {
        shortName: "Inositol", subtitle: "Myo + D-chiro, 40:1", dose: "2,050 mg",
        image: asset("v10-ova-inositol.webp"), alt: "Ingredient-inspired white powder in a clear glass dish with blue reflections",
        caption: "Myo + D-chiro inositol", kicker: "40:1 INOSITOL BLEND", name: "Myo + D-Chiro Inositol",
        copy: "2,000 mg myo-inositol plus 50 mg D-chiro-inositol per sachet: 2,050 mg combined, in a 40:1 ratio.",
      },
      {
        shortName: "Berberine", subtitle: "Berberine HCl", dose: "300 mg",
        image: asset("v10-ova-berberine.webp"), alt: "Golden yellow powder arranged on a clear blue glass dish",
        caption: "Berberine HCl", kicker: "BERBERINE HCL", name: "Berberine HCl",
        copy: "300 mg of berberine HCl in each daily sachet, alongside the inositol blend and other listed nutrients.",
      },
      {
        shortName: "Folate", subtitle: "Quatrefolic® L-5-MTHF", dose: "400 mcg",
        image: asset("v10-ova-folate.webp"), alt: "Ingredient-inspired pale green glass crystals in cool blue light",
        caption: "Quatrefolic® active folate", kicker: "ACTIVE FOLATE", name: "Quatrefolic® (L-5-MTHF)",
        copy: "400 mcg of Quatrefolic® L-5-MTHF per sachet. L-5-MTHF is the active folate form listed in the formula.",
      },
      {
        shortName: "Magnesium", subtitle: "Bisglycinate form", dose: "100 mg",
        image: asset("v10-ova-magnesium.webp"), alt: "Ingredient-inspired white mineral textures on a blue glass surface",
        caption: "Magnesium bisglycinate", kicker: "BISGLYCINATE FORM", name: "Magnesium Bisglycinate",
        copy: "100 mg of magnesium bisglycinate in each daily sachet. The formula also includes zinc bisglycinate and chromium picolinate, listed below.",
      },
      {
        shortName: "Vitamin D3", subtitle: "Cholecalciferol", dose: "600 IU",
        image: asset("v10-ova-d3.webp"), alt: "Ingredient-inspired golden oil droplet on clear glass in cool blue light",
        caption: "Vitamin D3", kicker: "CHOLECALCIFEROL", name: "Vitamin D3 (Cholecalciferol)",
        copy: "600 IU of vitamin D3 (cholecalciferol) per daily sachet, equivalent to 15 mcg.",
      },
    ],
    otherIngredients: [
      { name: "Zinc bisglycinate", dose: "10 mg" },
      { name: "Chromium picolinate", dose: "200 mcg" },
    ],
  },
};
